import React, { useCallback, useMemo, useState } from 'react';

import type { SignatureDetectionPendingInput } from '@aline/common';
import { getIsSignatureDetectionPending, MimeType } from '@aline/common';
import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { capitalize } from 'lodash';
import { Ban, ChevronDown, CircleCheck, FilePen, LayoutTemplate, Signature } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { DeclarativeMenu } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { IconColors } from 'src/components/icons/icon-utils';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { SmallTemplateIcon } from 'src/components/icons/SmallTemplateIcon';
import { SmallWorkflowDraftingIcon } from 'src/components/icons/SmallWorkflowDraftingIcon';
import { SmallWorkflowSingedIcon } from 'src/components/icons/SmallWorkflowSingedIcon';
import { StopIcon } from 'src/components/icons/StopIcon';
import {
  useStateChipStyles,
  useWorkflowStateIconStyles,
} from 'src/components/workflow/workflow-list/cells/WorkflowListStateCell';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { DocumentStatus } from 'src/gql/generated/apollo-types';
import type { DocStatusProperties } from 'src/helpers';
import { getDocStatusLabelProps } from 'src/helpers';
import { themeV5 } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import {
  getDisplayedDocumentStatus,
  getGridDocumentStatusMenuItems,
  getGridRowCanEditStatus,
  getStatusChangeNeedsConfirm,
  patchSetDocumentsStatusCache,
} from './grid-document-status';
import type { GridDocumentStatusInput } from './grid-document-status';
import { useCellStyles } from './ReportCell';
import type { ReportGridRow, ReportRow } from '../types';
import { getIsReportGridRow } from '../types';

interface StyleProps {
  color: string;
}

const useStyles = makeStyles<StyleProps>()((theme, { color }) => ({
  circle: {
    backgroundColor: color,
    borderRadius: '50%',
    height: 12,
    marginRight: theme.spacing(1),
    width: 12,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
  },
  statusText: {
    color,
  },
}));

const DOCUMENT_STATUS_OPTIONS = [
  Apollo.DocumentStatus.Draft,
  Apollo.DocumentStatus.Signed,
  Apollo.DocumentStatus.Expired,
  Apollo.DocumentStatus.Template,
] as const;

const DOCUMENT_STATUS_ICONS = {
  [Apollo.DocumentStatus.Draft]: FilePen,
  [Apollo.DocumentStatus.Expired]: Ban,
  [Apollo.DocumentStatus.Signed]: Signature,
  [Apollo.DocumentStatus.Template]: LayoutTemplate,
} satisfies Record<Apollo.DocumentStatus, LucideIcon>;

const STATUS_TRIGGER_HEIGHT_PX = 24;

const AI_SET_STATUS_TOOLTIP = 'Set by Aline AI from a detected signature';

interface RowDetectingStatusInput extends SignatureDetectionPendingInput {
  status: Apollo.DocumentStatus | null;
}

const useStatusControlStyles = makeStyles()(theme => ({
  option: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: 500,
  },
  placeholder: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
  // Matches the trigger's box so read-only and editable statuses line up, minus the button chrome.
  readOnly: {
    alignItems: 'center',
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    display: 'inline-flex',
    height: STATUS_TRIGGER_HEIGHT_PX,
    lineHeight: '16px',
    padding: theme.spacing(0.25, 0.75),
  },
  trigger: {
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.text.secondary,
    },
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: theme.spacing(0.5),
    height: STATUS_TRIGGER_HEIGHT_PX,
    /**
     * A data grid cell sets its line-height to the row height so plain text sits
     * centered. This chip centers its own contents, so it has to drop that or the
     * inherited strut stretches it to the full height of the row.
     */
    lineHeight: '16px',
    padding: theme.spacing(0.25, 0.75),
  },
  triggerChevron: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  // Hugs the trigger so the cell can center it, rather than inheriting the row-height line box.
  triggerWrapper: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: theme.spacing(0.75),
  },
}));

type GridStatusCellParams = GridCellParams<ReportRow, Apollo.DocumentStatus | null, string>;

/** The document fields the Status cell reads, so a grid of plain `Document` rows can render it too. */
export type DocumentStatusRow = Pick<
  ReportGridRow,
  | 'coreAiPropertiesExtractionFailedAt'
  | 'coreAiPropertiesExtractionFinishedAt'
  | 'coreAiPropertiesExtractionStartedAt'
  | 'isCertificate'
  | 'isSigned'
  | 'isSignedAiGenerated'
  | 'latestVersion'
  | 'requesterRole'
  | 'status'
  | 'variant'
  | 'workflow'
  | 'workflowFileRole'
>;

/**
 * Only for report grids, whose rows carry `documentId`. A grid of plain `Document` rows
 * renders {@link DocumentStatusRowControl} with the row's `id` instead.
 */
export function GridStatusCell({ row }: GridStatusCellParams): JSX.Element | null {
  const cellClasses = useCellStyles();

  if (!getIsReportGridRow(row)) return <div className={cellClasses.cell} />;

  return (
    <div className={cellClasses.cell}>
      <DocumentStatusRowControl documentId={row.documentId} row={row} />
    </div>
  );
}

export function DocumentStatusRowControl({
  documentId,
  row,
}: {
  documentId: UUID;
  row: DocumentStatusRow;
}): JSX.Element {
  const currentUser = useSelectCurrentUser();
  const { latestVersion } = row;
  const isStaticFile = latestVersion.__typename === 'StaticFileVersion';
  const isDetectingStatus = getIsRowDetectingStatus({
    detection: isStaticFile ? latestVersion.signatureDetection : null,
    document: row,
    isEnabled: Boolean(currentUser?.org.signatureDetectionEnabled),
    isPdf: isStaticFile && latestVersion.fileLocation.mimetype === MimeType.PDF,
    status: row.status,
  });
  // Same treatment as "Ai setting type...": the AI is deciding, so there is nothing to edit yet.
  if (isDetectingStatus)
    return (
      <>
        <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
        <Typography noWrap variant="body1">
          Ai detecting status...
        </Typography>
      </>
    );

  return (
    <DocumentStatusMenu
      documentId={documentId}
      isAiGenerated={Boolean(row.isSignedAiGenerated && row.isSigned)}
      statusInput={row}
    />
  );
}

/**
 * Whether a row shows "Ai detecting status..." instead of its status. Only a
 * Draft is waiting on the AI: detection never changes any other status, so a
 * Signed or Expired row keeps showing (and editing) its own.
 */
export function getIsRowDetectingStatus({ status, ...pending }: RowDetectingStatusInput): boolean {
  if (status !== Apollo.DocumentStatus.Draft) return false;
  return getIsSignatureDetectionPending(pending);
}

export function DocumentStatusMenu({
  documentId,
  isAiGenerated = false,
  statusInput,
}: {
  documentId: UUID;
  statusInput: GridDocumentStatusInput;
  /** Signature detection set this status. The chip says so on hover until someone changes it. */
  isAiGenerated?: boolean;
}): JSX.Element {
  const status = getDisplayedDocumentStatus(statusInput.status);
  if (getGridRowCanEditStatus(statusInput))
    return (
      <EditableDocumentStatusMenu
        documentId={documentId}
        isAiGenerated={isAiGenerated}
        status={status}
        statusInput={statusInput}
      />
    );

  return <ReadOnlyDocumentStatus isAiGenerated={isAiGenerated} status={status} />;
}

function AiSetStatusTooltip({
  children,
  enabled,
}: {
  children: JSX.Element;
  enabled: boolean;
}): JSX.Element {
  if (!enabled) return children;

  return (
    <AlineTooltip arrow placement="top" title={AI_SET_STATUS_TOOLTIP}>
      {children}
    </AlineTooltip>
  );
}

function ReadOnlyDocumentStatus({
  isAiGenerated,
  status,
}: {
  isAiGenerated: boolean;
  status: Apollo.DocumentStatus;
}): JSX.Element {
  const { classes } = useStatusControlStyles();

  return (
    <AiSetStatusTooltip enabled={isAiGenerated}>
      <span className={classes.readOnly}>
        <DocumentStatusOption status={status} />
      </span>
    </AiSetStatusTooltip>
  );
}

function EditableDocumentStatusMenu({
  documentId,
  isAiGenerated,
  status,
  statusInput,
}: {
  documentId: UUID;
  isAiGenerated: boolean;
  status: Apollo.DocumentStatus;
  statusInput: GridDocumentStatusInput;
}): JSX.Element {
  const { classes } = useStatusControlStyles();
  const [pendingStatus, setPendingStatus] = useState<Apollo.DocumentStatus | null>(null);
  const [setDocumentsStatus, { loading }] = Apollo.useSetDocumentsStatusMutation();
  const items = useMemo(() => getGridDocumentStatusMenuItems(statusInput), [statusInput]);
  const applyStatus = useCallback(
    async (target: Apollo.DocumentStatus): Promise<void> => {
      const { data } = await setDocumentsStatus({
        update(cache, result) {
          const payload = result.data?.setDocumentsStatus;
          if (!payload) return;

          patchSetDocumentsStatusCache(cache, payload);
        },
        variables: {
          input: {
            documentIds: [documentId],
            status: target,
          },
        },
      });
      setPendingStatus(null);
      if (!data?.setDocumentsStatus) return;
    },
    [documentId, setDocumentsStatus]
  );
  const selectStatus = useCallback(
    (target: Apollo.DocumentStatus) => {
      const item = items.find(entry => entry.status === target);
      if (!item || item.eligibility.outcome !== 'apply') return;
      if (getStatusChangeNeedsConfirm(status, target)) {
        setPendingStatus(target);
        return;
      }
      void applyStatus(target);
    },
    [applyStatus, items, status]
  );
  const menu = useMemo(
    () =>
      buildDocumentStatusMenu(
        items.map(item => ({
          enabled: !loading && item.eligibility.outcome !== 'skip',
          selected: item.eligibility.outcome === 'noop',
          status: item.status,
          tooltip: item.tooltip,
        })),
        selectStatus
      ),
    [items, loading, selectStatus]
  );

  return (
    <>
      <AiSetStatusTooltip enabled={isAiGenerated}>
        <span
          className={classes.triggerWrapper}
          onClick={stopGridClick}
          onMouseDown={stopGridClick}
        >
          <DeclarativeMenu config={menu}>
            {(renderProps, state) => (
              <StatusMenuTrigger
                ariaLabel="Change status"
                buttonProps={renderProps}
                disabled={state.disabled}
                label={<DocumentStatusOption status={status} />}
              />
            )}
          </DeclarativeMenu>
        </span>
      </AiSetStatusTooltip>
      <ConfirmationDialog
        message={`Change this document to ${pendingStatus ? capitalize(pendingStatus) : 'the selected status'}?`}
        open={Boolean(pendingStatus)}
        primaryText="Change Status"
        secondaryText="Cancel"
        title="Change status?"
        onClickPrimary={() => {
          if (!pendingStatus) return;
          void applyStatus(pendingStatus);
        }}
        onClose={() => setPendingStatus(null)}
      />
    </>
  );
}

export function DocumentStatusOption({ status }: { status: Apollo.DocumentStatus }): JSX.Element {
  const statusLabel = getDocStatusLabelProps(status);
  const Icon = DOCUMENT_STATUS_ICONS[status];
  const { classes } = useStatusControlStyles();

  return (
    <span className={classes.option}>
      <Icon color={statusLabel.color} size={16} strokeWidth={2} />
      <span className={classes.optionLabel}>{statusLabel.label}</span>
    </span>
  );
}

export function DocumentStatusSelect({
  disabled = false,
  id,
  onChange,
  value,
}: {
  onChange: (status: Apollo.DocumentStatus) => void;
  value: Apollo.DocumentStatus | null;
  disabled?: boolean;
  id?: string;
}): JSX.Element {
  const menu = useMemo(
    () =>
      buildDocumentStatusMenu(
        DOCUMENT_STATUS_OPTIONS.map(status => ({
          enabled: true,
          selected: status === value,
          status,
          tooltip: '',
        })),
        onChange
      ),
    [onChange, value]
  );

  return (
    <DeclarativeMenu config={menu} disabled={disabled} id={id} zIndex={themeV5.zIndex.modal + 2}>
      {(renderProps, state) => (
        <StatusMenuTrigger
          buttonProps={renderProps}
          disabled={disabled || state.disabled}
          label={<DocumentStatusSelectLabel value={value} />}
        />
      )}
    </DeclarativeMenu>
  );
}

function DocumentStatusSelectLabel({
  value,
}: {
  value: Apollo.DocumentStatus | null;
}): JSX.Element {
  const { classes } = useStatusControlStyles();
  if (!value) return <span className={classes.placeholder}>Select status</span>;

  return <DocumentStatusOption status={value} />;
}

export function DocumentStatusChip({
  status,
}: {
  status: Apollo.DocumentStatus;
}): JSX.Element | null {
  const { color: backgroundColor } = getDocStatusLabelProps(status);
  const { classes } = useStateChipStyles({ backgroundColor });

  return (
    <div className={classes.container}>
      <DocumentStatusChipIcon backgroundColor={backgroundColor} status={status} />
      <Typography noWrap className={classes.text}>
        {capitalize(status)}
      </Typography>
    </div>
  );
}

enum StatusFilterExtra {
  All = 'All',
  Mixed = 'Mixed',
}
export type DocumentStatusFilterType = Apollo.DocumentStatus | StatusFilterExtra;
export const DocumentStatusFilter = { ...DocumentStatus, ...StatusFilterExtra };

export function DocumentStatusSimple({
  status,
}: {
  status: DocumentStatusFilterType;
}): JSX.Element {
  const { color, label } = getStatusFilterProps(status);
  const { classes } = useStyles({ color });

  return (
    <div className={classes.container}>
      <div className={classes.circle} />
      <Typography className={classes.statusText} variant="body1">
        {label}
      </Typography>
    </div>
  );
}

export function DocumentStatusChipIcon({
  backgroundColor,
  status,
}: {
  backgroundColor: string;
  status: Apollo.DocumentStatus;
}): JSX.Element {
  const { classes } = useWorkflowStateIconStyles({ backgroundColor });

  return (
    <div className={classes.iconContainer}>
      <DocumentStatusIcon status={status} />
    </div>
  );
}

export function DocumentStatusIcon({ status }: { status: Apollo.DocumentStatus }): JSX.Element {
  switch (status) {
    case Apollo.DocumentStatus.Draft:
      return <SmallWorkflowDraftingIcon fill={IconColors.White} />;
    case Apollo.DocumentStatus.Signed:
      return <SmallWorkflowSingedIcon fill={IconColors.White} />;
    case Apollo.DocumentStatus.Template:
      return <SmallTemplateIcon fill={IconColors.White} />;
    case Apollo.DocumentStatus.Expired:
      return <StopIcon stroke={IconColors.White} />;
  }
}

interface DocumentStatusMenuEntry {
  enabled: boolean;
  selected: boolean;
  status: Apollo.DocumentStatus;
  tooltip: string;
}

function StatusMenuTrigger({
  ariaLabel,
  buttonProps,
  disabled = false,
  label,
}: {
  buttonProps: React.ComponentProps<'button'>;
  label: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
}): JSX.Element {
  const { classes } = useStatusControlStyles();

  return (
    <button
      {...buttonProps}
      aria-label={ariaLabel}
      className={classes.trigger}
      disabled={disabled}
      type="button"
    >
      {label}
      <ChevronDown className={classes.triggerChevron} size={14} />
    </button>
  );
}

function stopGridClick(event: React.MouseEvent): void {
  event.stopPropagation();
}

function buildDocumentStatusMenu(
  entries: readonly DocumentStatusMenuEntry[],
  onSelect: (status: Apollo.DocumentStatus) => void
): DeclarativeMenuConfig {
  const draft = findDocumentStatusMenuEntry(entries, Apollo.DocumentStatus.Draft);
  const signed = findDocumentStatusMenuEntry(entries, Apollo.DocumentStatus.Signed);
  const expired = findDocumentStatusMenuEntry(entries, Apollo.DocumentStatus.Expired);
  const template = findDocumentStatusMenuEntry(entries, Apollo.DocumentStatus.Template);
  if (!draft || !signed || !expired || !template) return [];

  return [
    documentStatusMenuItem(draft, onSelect),
    {
      icon: documentStatusMenuIcon(Apollo.DocumentStatus.Signed),
      label: 'Signed',
      submenu: [
        documentStatusMenuItem(signed, onSelect, {
          icon: documentStatusMenuIcon(Apollo.DocumentStatus.Signed, CircleCheck),
          label: 'Active',
        }),
        documentStatusMenuItem(expired, onSelect),
      ],
    },
    documentStatusMenuItem(template, onSelect),
  ];
}

function documentStatusMenuItem(
  entry: DocumentStatusMenuEntry,
  onSelect: (status: Apollo.DocumentStatus) => void,
  overrides?: { icon?: JSX.Element; label?: string }
): DeclarativeMenuConfig[number] {
  const statusLabel = getDocStatusLabelProps(entry.status);

  return {
    ...(entry.selected ? { checked: true } : null),
    ...(entry.tooltip ? { tooltip: entry.tooltip } : null),
    enabled: entry.enabled,
    icon: overrides?.icon ?? documentStatusMenuIcon(entry.status),
    label: overrides?.label ?? statusLabel.label,
    onClick: () => onSelect(entry.status),
  };
}

function documentStatusMenuIcon(
  status: Apollo.DocumentStatus,
  Icon: LucideIcon = DOCUMENT_STATUS_ICONS[status]
): JSX.Element {
  const { color } = getDocStatusLabelProps(status);
  return <Icon color={color} size={16} strokeWidth={2} />;
}

function findDocumentStatusMenuEntry(
  entries: readonly DocumentStatusMenuEntry[],
  status: Apollo.DocumentStatus
): DocumentStatusMenuEntry | undefined {
  return entries.find(entry => entry.status === status);
}

function getStatusFilterProps(
  documentStatusFilter: DocumentStatusFilterType
): Omit<DocStatusProperties, 'tooltipMsg'> {
  switch (documentStatusFilter) {
    case DocumentStatusFilter.All:
      return { color: themeV5.appColors.accent1, label: 'All' };
    case DocumentStatusFilter.Mixed:
      return { color: themeV5.appColors.accent1, label: 'Mixed' };
    default:
      return getDocStatusLabelProps(documentStatusFilter);
  }
}

enum DocumentStatusColors {
  Draft = '#FCDB62',
  Expired = 'rgb(203, 62, 62)',
  Signed = '#6876FF',
  Template = '#A2A5BE', // themeV5.appColors.reddish
}

export function getDocumentStatusColor({ status }: { status?: Apollo.DocumentStatus | null }) {
  switch (status) {
    case Apollo.DocumentStatus.Draft:
      return DocumentStatusColors.Draft;
    case Apollo.DocumentStatus.Signed:
      return DocumentStatusColors.Signed;
    case Apollo.DocumentStatus.Template:
      return DocumentStatusColors.Template;
    case Apollo.DocumentStatus.Expired:
      return DocumentStatusColors.Expired;
    default:
      return themeV5.appColors.accent1;
  }
}
